import { getDb } from '@/db/schema';
import type { FavoriteRow } from '@/db/schema';
import { deleteCredential, getAuth, listCredentialRows, setAuth } from '@/db/credentials';
import { mergeFavoriteRows, normalizeFavoriteRow } from '@/db/favorites';
import { PassphraseSession } from '@/lib/crypto';
import type { AccountDataBundle } from './accountDataSync';
import { notifyAccountDataRestored } from './accountDataEvents';

const ACCOUNT_SYNC_STORES = [
  'favorites',
  'history',
  'blacklist',
  'prefs',
  'savedSearches',
] as const;

type AccountSyncStore = (typeof ACCOUNT_SYNC_STORES)[number];

export interface AccountDataMergeResult {
  favorites: number;
  history: number;
  blacklist: number;
  prefs: number;
  savedSearches: number;
  sourceAuth: number;
}

function emptyMergeResult(): AccountDataMergeResult {
  return {
    favorites: 0,
    history: 0,
    blacklist: 0,
    prefs: 0,
    savedSearches: 0,
    sourceAuth: 0,
  };
}

export function countAccountDataMergeRows(result: AccountDataMergeResult): number {
  return (
    result.favorites +
    result.history +
    result.blacklist +
    result.prefs +
    result.savedSearches +
    result.sourceAuth
  );
}

export async function snapshotAccountDataBundle(): Promise<AccountDataBundle> {
  const db = await getDb();
  const sourceAuth: AccountDataBundle['sourceAuth'] = [];
  if (PassphraseSession.isUnlocked()) {
    for (const row of await listCredentialRows()) {
      const auth = await getAuth(row.source);
      if (!auth) continue;
      const entry: AccountDataBundle['sourceAuth'][number] = {
        source: row.source,
        auth,
      };
      if (row.username !== undefined) entry.username = row.username;
      if (row.mode !== undefined) entry.mode = row.mode;
      sourceAuth.push(entry);
    }
  }
  return {
    schemaVersion: 1,
    exportedAt: Date.now(),
    stores: {
      favorites: await db.getAll('favorites'),
      history: await db.getAll('history'),
      blacklist: await db.getAll('blacklist'),
      prefs: await db.getAll('prefs'),
      savedSearches: await db.getAll('savedSearches'),
    },
    sourceAuth,
  };
}

export async function restoreAccountDataBundle(bundle: AccountDataBundle): Promise<void> {
  const db = await getDb();
  for (const name of ACCOUNT_SYNC_STORES) {
    const rows = bundle.stores[name];
    if (!Array.isArray(rows)) continue;
    const tx = db.transaction(name, 'readwrite');
    await tx.store.clear();
    for (const row of rows) {
      if (name === 'favorites') {
        await tx.store.put(normalizeFavoriteRow(row as FavoriteRow));
        continue;
      }
      await tx.store.put(row as never);
    }
    await tx.done;
  }
  const restoredSources = new Set(bundle.sourceAuth.map((entry) => entry.source));
  for (const row of await listCredentialRows()) {
    if (!restoredSources.has(row.source)) {
      await deleteCredential(row.source);
    }
  }
  for (const entry of bundle.sourceAuth) {
    await setAuth(entry.source, entry.auth, {
      username: entry.username,
    });
  }
  notifyAccountDataRestored();
}

export async function mergeAccountDataBundle(bundle: AccountDataBundle): Promise<AccountDataMergeResult> {
  const db = await getDb();
  const result = emptyMergeResult();

  for (const row of bundle.stores.favorites) {
    const existing = await db.get('favorites', row.key);
    if (!existing) {
      await db.put('favorites', normalizeFavoriteRow(row));
      result.favorites += 1;
      continue;
    }
    const merged = mergeFavoriteRows(existing, row);
    if (JSON.stringify(merged) === JSON.stringify(existing)) continue;
    await db.put('favorites', merged);
    result.favorites += 1;
  }

  for (const row of bundle.stores.history) {
    const existing = await db.get('history', row.key);
    if (existing && existing.lastSeen >= row.lastSeen) continue;
    await db.put('history', row);
    result.history += 1;
  }

  for (const row of bundle.stores.blacklist) {
    if (await db.get('blacklist', row.key)) continue;
    await db.put('blacklist', row);
    result.blacklist += 1;
  }

  for (const row of bundle.stores.prefs) {
    if (await db.get('prefs', row.key)) continue;
    await db.put('prefs', row);
    result.prefs += 1;
  }

  for (const row of bundle.stores.savedSearches) {
    if (await db.get('savedSearches', row.id)) continue;
    await db.put('savedSearches', row);
    result.savedSearches += 1;
  }

  const existingCredentialSources = new Set(
    (await listCredentialRows()).map((row) => row.source),
  );
  for (const entry of bundle.sourceAuth) {
    if (existingCredentialSources.has(entry.source)) continue;
    await setAuth(entry.source, entry.auth, {
      username: entry.username,
    });
    result.sourceAuth += 1;
  }

  if (countAccountDataMergeRows(result) > 0) {
    notifyAccountDataRestored();
  }
  return result;
}

export async function hasMeaningfulLocalAccountData(): Promise<boolean> {
  const db = await getDb();
  for (const name of ACCOUNT_SYNC_STORES) {
    if ((await db.count(name)) > 0) return true;
  }
  return (await listCredentialRows()).length > 0;
}

export function countAccountBundleRows(bundle: AccountDataBundle): number {
  return ACCOUNT_SYNC_STORES.reduce(
    (total, store: AccountSyncStore) => total + bundle.stores[store].length,
    0,
  ) + bundle.sourceAuth.length;
}
